import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useLang } from "../../context/LangContext";
import { translations } from "../../lib/i18n";
import { TEST_IDS } from "../../constants/testIds";

const useCountUp = (target, start) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const duration = 1400;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target]);
  return val;
};

const StatCard = ({ i, prefix, value, label, inView, wide }) => {
  const count = useCountUp(value, inView);
  return (
    <motion.div
      data-testid={TEST_IDS.impact.stat(i)}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: i * 0.08 }}
      className={`border-t border-white/10 pt-8 ${wide ? "lg:col-span-2" : ""}`}
    >
      <div className="font-display font-extrabold text-white leading-none flex items-baseline gap-1"
           style={{ fontSize: "clamp(3rem, 6vw, 5.25rem)" }}>
        <span className="text-[color:var(--rp-accent)] text-[0.55em] translate-y-[-0.5em] font-semibold">
          {prefix}
        </span>
        <span>{count}</span>
      </div>
      <div className="mt-5 text-[11px] tracking-[0.22em] uppercase text-[color:var(--rp-muted)] max-w-[36ch] leading-relaxed">
        {label}
      </div>
    </motion.div>
  );
};

const Impact = () => {
  const { lang } = useLang();
  const t = translations[lang].impact;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="impact"
      data-testid={TEST_IDS.impact.root}
      className="rp-section bg-[color:var(--rp-bg)]"
      ref={ref}
    >
      <div className="rp-container">
        <div className="grid grid-cols-12 gap-8 mb-14">
          <div className="col-span-12 lg:col-span-4">
            <div className="rp-eyebrow mb-4">01 · Impact</div>
            <h2 className="rp-heading rp-headline">{t.title}</h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="rp-lede">{t.subtitle}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-x-8 gap-y-10">
          {t.stats.map((s, i) => (
            <StatCard
              key={i}
              i={i}
              inView={inView}
              prefix={s.prefix}
              value={s.value}
              label={s.label}
              wide={i >= 3}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Impact;
